<template>
  <BaseModal size="medium" :open="open" @close="dismiss">
    <template #header>
      {{ consentDetail?.consentTitle || "Consent Details" }}
    </template>

    <template #default>
      <!-- Loading state -->
      <div v-if="isLoading" class="flex min-h-32 items-center justify-center">
        <Icon name="spinner-third-duotone" class="size-8 animate-spin" />
      </div>

      <!-- Error state -->
      <div
        v-else-if="fetchError"
        class="flex min-h-32 flex-col items-center justify-center gap-3"
      >
        <p class="text-sm text-red-600">{{ fetchError }}</p>
        <button class="btn btn-outline" @click="consentQuery.refetch()">
          Retry
        </button>
      </div>

      <!-- Content -->
      <div v-else-if="consentDetail" class="space-y-4">
        <div>
          <p class="text-sm font-medium text-gray-500">Feature</p>
          <p class="text-gray-900">{{ consentDetail.featureName }}</p>
        </div>

        <div>
          <p class="text-sm font-medium text-gray-500">Description</p>
          <p class="text-gray-900">{{ consentDetail.consentDescription }}</p>
        </div>

        <div class="border-t border-gray-200 pt-4">
          <p class="mb-2 text-sm font-medium text-gray-500">
            Full Consent Text
          </p>
          <div
            class="max-h-64 overflow-y-auto rounded-md bg-gray-50 p-4 text-sm text-gray-700"
          >
            <pre class="font-sans whitespace-pre-wrap">{{
              consentDetail.consentFullText
            }}</pre>
          </div>
        </div>

        <!-- Current status -->
        <div class="border-t border-gray-200 pt-4">
          <p class="text-sm font-medium text-gray-500">Current Status</p>
          <div class="mt-1 flex items-center gap-2">
            <span
              v-if="
                consentDetail.isConsented && !consentDetail.requiresReconsent
              "
              class="inline-flex items-center text-green-700"
            >
              <Icon name="check-circle" class="mr-1 size-5" />
              Consented
            </span>
            <span
              v-else-if="consentDetail.requiresReconsent"
              class="inline-flex items-center text-yellow-700"
            >
              <Icon name="exclamation-circle" class="mr-1 size-5" />
              Re-consent Required (terms have been updated)
            </span>
            <span v-else class="inline-flex items-center text-gray-500">
              <Icon name="times-circle" class="mr-1 size-5" />
              Not Consented
            </span>
          </div>
          <p
            v-if="consentDetail.isConsented && consentDetail.consentedBy"
            class="mt-1 text-sm text-gray-500"
          >
            Consented by {{ consentDetail.consentedBy.name }}
            <span v-if="consentDetail.consentedAt">
              on {{ formatDate(consentDetail.consentedAt) }}
            </span>
          </p>
        </div>
      </div>
    </template>

    <template #footer>
      <p
        v-if="!consentError && accessNote"
        class="mr-auto text-sm text-gray-500"
      >
        {{ accessNote }}
      </p>
      <p v-if="consentError" class="mr-auto text-sm text-red-600">
        {{ consentError }}
      </p>
      <button class="btn btn-outline" @click="dismiss">Close</button>
      <!-- `isConsentable`, the same field the card's button uses and the same
           value `POST /give/` resolves: the org's own entitlement, without the
           personal override that can raise `isAvailable` for one user. Gating
           on availability instead put a manager in front of the full legal
           terms and an "I Agree" the API could only answer with a 403. -->
      <button
        v-if="
          props.isManager &&
          consentDetail &&
          consentDetail.isConsentable &&
          (!consentDetail.isConsented || consentDetail.requiresReconsent)
        "
        class="btn-primary"
        :disabled="giveConsentMutation.isPending"
        @click="handleGiveConsent"
      >
        <Icon
          v-if="giveConsentMutation.isPending"
          name="spinner-third-duotone"
          class="mr-2 size-4 animate-spin"
        />
        I Agree
      </button>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import BaseModal from "@/components/Modal/BaseModal.vue";
import Icon from "@/components/Core/Icon.vue";
import {
  useConsentDetailQuery,
  useGiveConsentMutation,
} from "@/services/consent";
import { formatDrfRefusal } from "@/utils/drf-errors";
import { DateTime } from "luxon";

const props = defineProps({
  isManager: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  "confirm-consent": () => true,
  // Closed by the user without consenting; not emitted by the close that
  // follows a successful consent.
  dismiss: () => true,
});

const open = ref(false);
const currentFeatureKey = ref("");

// The service owns this endpoint: organization-scoped keying, auth gating,
// the availability overlay, and the race handling this component used to do
// by hand with a request-id counter. The key is reactive because the modal is
// opened on a different feature each time.
const consentQuery = useConsentDetailQuery(currentFeatureKey, {
  enabled: open,
});
const consentDetail = computed(() => consentQuery.data ?? null);
const isLoading = computed(
  () => consentQuery.isPending && !!currentFeatureKey.value,
);
const fetchError = computed(() =>
  consentQuery.isError
    ? "Failed to load consent details. Please try again."
    : null,
);

const giveConsentMutation = useGiveConsentMutation();

const consentError = ref<string | null>(null);

// The same two questions the card's row answers: `isConsentable` is the plan,
// `isAvailable` is this caller, and a `UserFeatureFlag` can put them either
// side of each other.
const accessNote = computed<string | null>(() => {
  const detail = consentDetail.value;
  if (!detail) {
    return null;
  }
  if (detail.isConsentable) {
    return detail.isAvailable
      ? null
      : "Included in your plan, but switched off for your account. The consent recorded here is the organization's, and applies to everyone.";
  }
  if (props.isManager && detail.isAvailable && !detail.isConsented) {
    return "Enabled for your account only. Consent is recorded for the whole organization, so it can't be accepted until the feature is part of your plan.";
  }
  return null;
});

function openModal(featureKey: string): void {
  currentFeatureKey.value = featureKey;
  consentError.value = null;
  // Opening re-enables the query (see its `enabled` gate), which is also what
  // retries after a failure: reopening the same feature changes no query key,
  // so without the gate nothing would be requested and the modal would render
  // the stale error again — the imperative fetch this replaced ran on every
  // open. One mechanism, not two: an explicit refetch here would double the
  // request for a feature that simply changed.
  open.value = true;
}

function closeModal(): void {
  open.value = false;
  consentError.value = null;
}

function dismiss(): void {
  closeModal();
  emit("dismiss");
}

async function handleGiveConsent(): Promise<void> {
  if (!currentFeatureKey.value) {
    return;
  }
  consentError.value = null;
  try {
    await giveConsentMutation.mutateAsync(currentFeatureKey.value);
    emit("confirm-consent");
    closeModal();
  } catch (error) {
    // A 403 here is the entitlement refusal, which "please try again" would
    // send a manager round a loop that cannot end.
    consentError.value = formatDrfRefusal(
      error,
      "Failed to give consent. Please try again.",
    );
  }
}

function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
}

defineExpose({
  openModal,
  closeModal,
});
</script>
